import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';

const defaultMenuStyle = {
  position: 'absolute',
  top: '100%',
  left: 0,
  right: 0,
  backgroundColor: '#ffffff',
  border: '1px solid #e5e7eb',
  borderRadius: '8px',
  marginTop: '6px',
  boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.08), 0 4px 6px -4px rgba(0, 0, 0, 0.08)',
  maxHeight: '220px',
  overflowY: 'auto',
  zIndex: 50
};

const defaultItemStyle = {
  padding: '10px 12px',
  cursor: 'pointer',
  fontSize: '14px',
  color: '#111827',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis'
};

function defaultFilter(query, option) {
  const q = String(query || '').trim().toLowerCase();
  if (!q) return true;
  return String(option || '').toLowerCase().includes(q);
}

export default function AutocompleteInput({
  value,
  onChange,
  options,
  placeholder,
  inputProps,
  wrapperStyle,
  menuStyle,
  itemStyle,
  maxItems
}) {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  const normalizedOptions = useMemo(() => {
    const arr = Array.isArray(options) ? options : [];
    const unique = Array.from(new Set(arr.map((o) => String(o)).filter(Boolean)));
    const filtered = unique.filter((opt) => defaultFilter(value, opt));
    return filtered.slice(0, Math.max(0, maxItems || 30));
  }, [options, value, maxItems]);

  useEffect(() => {
    const onDocMouseDown = (e) => {
      if (!wrapperRef.current) return;
      if (!wrapperRef.current.contains(e.target)) {
        setOpen(false);
        setActiveIndex(-1);
      }
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, []);

  const commit = (next) => {
    onChange(next);
    setOpen(false);
    setActiveIndex(-1);
    requestAnimationFrame(() => inputRef.current?.focus?.());
  };

  return (
    <div
      ref={wrapperRef}
      style={{
        position: 'relative',
        width: '100%',
        ...wrapperStyle
      }}
    >
      <input
        {...(inputProps || {})}
        ref={inputRef}
        value={value}
        placeholder={placeholder}
        onChange={(e) => {
          onChange(e.target.value);
          setOpen(true);
          setActiveIndex(-1);
          inputProps?.onChange?.(e);
        }}
        onFocus={(e) => {
          setOpen(true);
          inputProps?.onFocus?.(e);
        }}
        onBlur={(e) => {
          // Don't close here; option clicks use onMouseDown and we also close on outside click.
          inputProps?.onBlur?.(e);
        }}
        onKeyDown={(e) => {
          if (!open && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
            setOpen(true);
          }
          if (open && normalizedOptions.length > 0) {
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              setActiveIndex((i) => Math.min(i + 1, normalizedOptions.length - 1));
            } else if (e.key === 'ArrowUp') {
              e.preventDefault();
              setActiveIndex((i) => Math.max(i - 1, 0));
            } else if (e.key === 'Enter' && activeIndex >= 0) {
              e.preventDefault();
              commit(normalizedOptions[activeIndex]);
            } else if (e.key === 'Escape') {
              e.preventDefault();
              setOpen(false);
              setActiveIndex(-1);
            }
          }
          inputProps?.onKeyDown?.(e);
        }}
      />

      {open && normalizedOptions.length > 0 && (
        <div style={{ ...defaultMenuStyle, ...menuStyle }} role="listbox">
          {normalizedOptions.map((opt, idx) => {
            const isActive = idx === activeIndex;
            return (
              <div
                key={`${opt}-${idx}`}
                role="option"
                aria-selected={isActive}
                onMouseDown={(e) => {
                  // Prevent blur before we commit selection.
                  e.preventDefault();
                  commit(opt);
                }}
                onMouseEnter={() => setActiveIndex(idx)}
                style={{
                  ...defaultItemStyle,
                  ...itemStyle,
                  backgroundColor: isActive ? '#f3f4f6' : '#ffffff'
                }}
                title={opt}
              >
                {opt}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

AutocompleteInput.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(PropTypes.string),
  placeholder: PropTypes.string,
  inputProps: PropTypes.object,
  wrapperStyle: PropTypes.object,
  menuStyle: PropTypes.object,
  itemStyle: PropTypes.object,
  maxItems: PropTypes.number
};

